import { Smartphone, Share, Plus, Apple, Download, Sparkles, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import PlayBadge from '../components/PlayBadge';

export default function InstallGuide() {
    const navigate = useNavigate();
    const [device, setDevice] = useState('android');
    const isStandalone = typeof window !== 'undefined' && (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone);

    return (
        <div className="px-5 pt-10 pb-32 max-w-2xl mx-auto" data-testid="install-guide-page">
            <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-brand-ink3 hover:text-brand-ink mb-6">
                <ArrowLeft size={14} /> Back
            </button>

            <p className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3">Install</p>
            <h1 className="font-serif text-3xl sm:text-4xl text-brand-ink mt-1 mb-3">GrowDaily on your phone</h1>
            <p className="text-brand-ink2 mb-8">Get the official Android app on Google Play — or add GrowDaily to your home screen on iPhone and desktop.</p>

            {isStandalone && (
                <div className="mb-6 bg-brand-green/10 border border-brand-green/30 rounded-2xl p-4 text-sm text-brand-green inline-flex items-center gap-2">
                    <Sparkles size={16} /> You're already using GrowDaily as an app 🌱
                </div>
            )}

            <div className="flex gap-2 mb-6">
                <button
                    data-testid="tab-android"
                    onClick={() => setDevice('android')}
                    className={`flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-full text-sm transition-colors ${device === 'android' ? 'bg-brand-green text-white' : 'bg-brand-card border border-brand-border text-brand-ink hover:bg-brand-bg2'}`}
                >
                    <Smartphone size={14} /> Android
                </button>
                <button
                    data-testid="tab-ios"
                    onClick={() => setDevice('ios')}
                    className={`flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-full text-sm transition-colors ${device === 'ios' ? 'bg-brand-green text-white' : 'bg-brand-card border border-brand-border text-brand-ink hover:bg-brand-bg2'}`}
                >
                    <Apple size={14} /> iPhone / iPad
                </button>
                <button
                    data-testid="tab-desktop"
                    onClick={() => setDevice('desktop')}
                    className={`flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-full text-sm transition-colors ${device === 'desktop' ? 'bg-brand-green text-white' : 'bg-brand-card border border-brand-border text-brand-ink hover:bg-brand-bg2'}`}
                >
                    <Download size={14} /> Desktop
                </button>
            </div>

            {device === 'android' && (
                <div className="bg-brand-card border border-brand-border rounded-3xl p-6" data-testid="instr-android">
                    <h2 className="font-serif text-xl text-brand-ink mb-3">Android — get the official app</h2>
                    <p className="text-sm text-brand-ink2 mb-4">GrowDaily is on Google Play — the easiest way to install, with push notifications built in.</p>
                    <div className="mb-6"><PlayBadge /></div>
                    <h3 className="font-serif text-base text-brand-ink mb-3 mt-2">Alternative: install without the store (PWA)</h3>
                    <Step n={1} title="Open GrowDaily">You're already here — perfect.</Step>
                    <Step n={2} title="Tap the menu ⋮">Find the three dots at the top right.</Step>
                    <Step n={3} title="Choose 'Install app' or 'Add to Home screen'">Some browsers also show an automatic install banner at the bottom.</Step>
                    <Step n={4} title="Confirm 'Install'">GrowDaily lands on your home screen as a real app.</Step>
                    <Step n={5} title="Enable notifications (optional)">Open the app → Profile → tap 'Enable push' for watering and frost alerts.</Step>
                </div>
            )}

            {device === 'ios' && (
                <div className="bg-brand-card border border-brand-border rounded-3xl p-6" data-testid="instr-ios">
                    <h2 className="font-serif text-xl text-brand-ink mb-4">iPhone &amp; iPad (Safari)</h2>
                    <div className="mb-4 text-xs text-brand-ink3 bg-brand-ochre/10 border border-brand-ochre/30 rounded-2xl p-3">
                        ⚠️ Use <strong>Safari</strong> (not Chrome or Firefox on iOS). Chrome on iPhone cannot install PWAs.
                    </div>
                    <Step n={1} title="Open GrowDaily in Safari">Tap the address bar and make sure you're on the correct domain.</Step>
                    <Step n={2} title="Tap the Share button" icon={<Share size={16} className="text-brand-green" />}>The square icon with an up arrow — at the bottom on iPhone, top on iPad.</Step>
                    <Step n={3} title="Scroll down and choose 'Add to Home Screen'" icon={<Plus size={16} className="text-brand-green" />}>Find the icon with a plus sign.</Step>
                    <Step n={4} title="Tap 'Add' at the top right">The GrowDaily icon now appears on your home screen.</Step>
                    <Step n={5} title="Enable push (iOS 16.4+)">Open the app from the home screen (not from Safari!) → Profile → 'Enable push'. Requires iOS 16.4 or newer.</Step>
                </div>
            )}

            {device === 'desktop' && (
                <div className="bg-brand-card border border-brand-border rounded-3xl p-6" data-testid="instr-desktop">
                    <h2 className="font-serif text-xl text-brand-ink mb-4">Desktop (Chrome, Edge, Brave)</h2>
                    <Step n={1} title="Look for the install icon">A small icon with a down arrow in the address bar, on the right.</Step>
                    <Step n={2} title="Click the icon">Or open the menu ⋮ → 'Install GrowDaily'.</Step>
                    <Step n={3} title="Click 'Install'">The app opens in its own window and lands in your Programs/Applications folder.</Step>
                    <Step n={4} title="Pin it to taskbar/dock">Right-click the app icon → 'Pin to taskbar' (Windows) or 'Keep in Dock' (Mac).</Step>
                </div>
            )}

            <div className="mt-8 bg-brand-bg2 border border-brand-border rounded-3xl p-5">
                <h3 className="font-serif text-lg text-brand-ink mb-2">What is a PWA?</h3>
                <p className="text-sm text-brand-ink2 leading-relaxed">
                    PWA stands for <strong>Progressive Web App</strong> — a modern way to "install" a website as a real app, without going through the App Store or Google Play.
                    You get:
                </p>
                <ul className="mt-3 space-y-1.5 text-sm text-brand-ink2">
                    <li>✅ Icon on the home screen</li>
                    <li>✅ Opens in full screen (no browser bar)</li>
                    <li>✅ Push notifications</li>
                    <li>✅ Works offline (partially)</li>
                    <li>✅ Auto-updates in the background</li>
                </ul>
            </div>
        </div>
    );
}

function Step({ n, title, icon, children }) {
    return (
        <div className="flex gap-3 mb-4">
            <div className="w-7 h-7 rounded-full bg-brand-green text-white flex items-center justify-center text-xs font-semibold flex-shrink-0 mt-0.5">{n}</div>
            <div className="flex-1">
                <div className="font-serif text-base text-brand-ink inline-flex items-center gap-2">
                    {title} {icon}
                </div>
                <div className="text-sm text-brand-ink3 mt-0.5">{children}</div>
            </div>
        </div>
    );
}
